/**
 * Dialog di scelta del titolo per una transazione senza strumento fisso
 * (TODO.md): o tra quelli già a catalogo, o uno nuovo cercato su OpenFIGI
 * (ISIN, ticker o nome libero) e creato al volo dal candidato scelto.
 *
 * Stesso stile del dialog di risoluzione manuale del ticker in watchlist
 * (F-040): tabella di candidati con un bottone "Scegli" per riga.
 */

import { type FormEvent, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { Alert, Button, EmptyState, Field, Modal, TableWrap } from '@/ui';
import {
  useCreateInstrument,
  useResolveInstrumentCandidates,
  useSearchInstruments,
  type InstrumentCandidate,
  type InstrumentSearchResult,
} from './api';

type PickedInstrument = { id: string; name: string };

type Mode = 'catalog' | 'new';

type InstrumentPickerModalProps = {
  onSelect: (instrument: PickedInstrument) => void;
  onClose: () => void;
};

function CatalogSearch({ onSelect }: { onSelect: (instrument: PickedInstrument) => void }) {
  const [query, setQuery] = useState('');
  const search = useSearchInstruments();
  const error = search.error instanceof ApiError ? search.error : null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed) search.mutate(trimmed);
  };

  const results: InstrumentSearchResult[] = search.data?.items ?? [];

  return (
    <div className="instrument-picker__panel">
      <form className="instrument-picker__search" onSubmit={handleSubmit}>
        <Field
          label="Cerca per nome, ISIN o simbolo"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Apple, US0378331005, AAPL…"
        />
        <Button type="submit" variant="secondary" loading={search.isPending} disabled={!query.trim()}>
          Cerca
        </Button>
      </form>

      {error && (
        <Alert variant="error" title="Ricerca non riuscita">
          {error.message}
        </Alert>
      )}

      {search.isSuccess && results.length === 0 && (
        <EmptyState
          icon="🔍"
          title="Nessun titolo trovato"
          description="Prova con l'ISIN o il simbolo esatto, oppure aggiungine uno nuovo dall'altra scheda."
        />
      )}

      {results.length > 0 && (
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Nome</th>
                <th>ISIN / Simbolo</th>
                <th>Valuta</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {results.map((instrument) => (
                <tr key={instrument.id}>
                  <td>
                    {/* La scheda del titolo (SPEC-016) si apre in un'altra scheda:
                        il modal sta dentro un form di transazione a metà. */}
                    <a
                      href={`/instruments/${instrument.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {instrument.name}
                    </a>
                  </td>
                  <td>{instrument.isin ?? instrument.symbol ?? '—'}</td>
                  <td>{instrument.currency}</td>
                  <td>
                    <Button variant="ghost" onClick={() => onSelect({ id: instrument.id, name: instrument.name })}>
                      Scegli
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function NewInstrumentSearch({ onSelect }: { onSelect: (instrument: PickedInstrument) => void }) {
  const [query, setQuery] = useState('');
  // OpenFIGI non restituisce sempre la valuta: verificato dal vivo che
  // `/v3/search` (ricerca libera per nome) non la porta praticamente mai,
  // a differenza di `/v3/mapping` da ISIN. Bloccare la scelta in quel caso
  // renderebbe la ricerca per nome di fatto inutilizzabile — si chiede quindi
  // conferma della valuta invece di indovinarla o disabilitare la riga.
  const [missingCurrencyFor, setMissingCurrencyFor] = useState<string | null>(null);
  const [manualCurrency, setManualCurrency] = useState('');
  const resolve = useResolveInstrumentCandidates();
  const create = useCreateInstrument();
  const resolveError = resolve.error instanceof ApiError ? resolve.error : null;
  const createError = create.error instanceof ApiError ? create.error : null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed) resolve.mutate(trimmed);
  };

  const createFromCandidate = (candidate: InstrumentCandidate, currency: string) => {
    create.mutate(
      {
        ticker: candidate.ticker,
        exchange: candidate.exchange,
        figi: candidate.figi,
        currency,
        securityType: candidate.securityType,
        name: candidate.name ?? undefined,
      },
      { onSuccess: (created) => onSelect({ id: created.id, name: created.name }) },
    );
  };

  const handlePick = (candidate: InstrumentCandidate) => {
    if (candidate.currency) {
      createFromCandidate(candidate, candidate.currency);
      return;
    }
    // Nessuna valuta da OpenFIGI per questo candidato: si chiede all'utente,
    // mai un valore indovinato per un dato che finisce nelle quotazioni.
    setManualCurrency('');
    setMissingCurrencyFor(candidate.ticker);
  };

  const handleConfirmManualCurrency = (candidate: InstrumentCandidate) => {
    const trimmed = manualCurrency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(trimmed)) return;
    createFromCandidate(candidate, trimmed);
  };

  const candidates: InstrumentCandidate[] = resolve.data?.items ?? [];

  return (
    <div className="instrument-picker__panel">
      <form className="instrument-picker__search" onSubmit={handleSubmit}>
        <Field
          label="ISIN, ticker o nome del titolo"
          hint="Un ISIN valido interroga OpenFIGI per identificativo; altrimenti è una ricerca libera per nome"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="US0378331005, AAPL, Apple…"
        />
        <Button type="submit" variant="secondary" loading={resolve.isPending} disabled={!query.trim()}>
          Cerca su OpenFIGI
        </Button>
      </form>

      {resolveError && (
        <Alert variant="error" title="Ricerca non riuscita">
          {resolveError.message}
        </Alert>
      )}
      {createError && (
        <Alert variant="error" title="Creazione del titolo non riuscita">
          {createError.message}
        </Alert>
      )}

      {resolve.isSuccess && candidates.length === 0 && (
        <EmptyState icon="🔍" title="Nessun candidato trovato" description="Prova un ISIN esatto o un altro termine di ricerca." />
      )}

      {candidates.length > 0 && (
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ticker</th>
                <th>Borsa</th>
                <th>Valuta</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {candidates.map((candidate, index) => (
                <tr key={`${candidate.ticker}-${candidate.exchange ?? ''}-${index}`}>
                  <td>{candidate.name ?? '—'}</td>
                  <td>{candidate.ticker}</td>
                  <td>{candidate.exchange ?? '—'}</td>
                  <td>{candidate.currency ?? '—'}</td>
                  <td className="instrument-picker__pick-cell">
                    {missingCurrencyFor === candidate.ticker ? (
                      <span className="instrument-picker__manual-currency">
                        <input
                          className="field__input"
                          aria-label={`Valuta per ${candidate.name ?? candidate.ticker}`}
                          value={manualCurrency}
                          onChange={(event) => setManualCurrency(event.target.value)}
                          placeholder="USD"
                          maxLength={3}
                        />
                        <Button
                          variant="ghost"
                          loading={create.isPending}
                          disabled={!/^[A-Za-z]{3}$/.test(manualCurrency.trim())}
                          onClick={() => handleConfirmManualCurrency(candidate)}
                        >
                          Conferma
                        </Button>
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        loading={create.isPending && create.variables?.ticker === candidate.ticker}
                        onClick={() => handlePick(candidate)}
                      >
                        Scegli
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

export function InstrumentPickerModal({ onSelect, onClose }: InstrumentPickerModalProps) {
  const [mode, setMode] = useState<Mode>('catalog');

  return (
    <Modal title="Scegli il titolo" onClose={onClose}>
      <div className="transaction-form__mode-switch" role="radiogroup" aria-label="Modalità di ricerca">
        <label className={mode === 'catalog' ? 'active' : ''}>
          <input type="radio" name="instrument-picker-mode" value="catalog" checked={mode === 'catalog'} onChange={() => setMode('catalog')} />
          Titolo già a catalogo
        </label>
        <label className={mode === 'new' ? 'active' : ''}>
          <input type="radio" name="instrument-picker-mode" value="new" checked={mode === 'new'} onChange={() => setMode('new')} />
          Titolo nuovo (OpenFIGI)
        </label>
      </div>

      {mode === 'catalog' ? <CatalogSearch onSelect={onSelect} /> : <NewInstrumentSearch onSelect={onSelect} />}
    </Modal>
  );
}
